import React, { useRef } from 'react';
import { Platform, StyleSheet, View, type ViewStyle } from 'react-native';
import { useImageTransform } from '../useImageTransform';
import { BboxLayer, type BboxLayerItem } from './BboxLayer';
import { MaskLayer, type MaskLayerItem } from './MaskLayer';
import { ControlsLayer, type ControlsLayerItem } from './ControlsLayer';
import { ZoomableStage, type ZoomableStageHandle } from './ZoomableStage';

/**
 * The image + overlay composite. Absolute layering (inside the
 * zoomable inner container, so pinch/pan transforms everything
 * together and coordinate math stays in visible-pixel space):
 *
 *   image           (0)
 *   mask layer      (1)  pointer-events: none
 *   bbox layer      (2)  pointer-events: none
 *   controls layer  (3)  pointer-events: box-none (chips catch,
 *                        background passes through)
 *   children        (4)  refine overlay / draw overlay / scanning
 *                        hero, mounted by the parent
 *
 * Caller passes items already filtered to the ones we want to
 * render. This component does NO filtering of its own.
 *
 * `fillParent` swaps the stage from fixed `aspectRatio: 4/3` to
 * `flex: 1` — used by the new fullscreen layout where the stage
 * is the viewport hero.
 */

export interface AnnotationItem {
  id: number;
  bbox: import('../useImageTransform').Bbox;
  /** NULL on the Gemini native-bbox path — bbox rectangle still
   *  draws, mask overlay is skipped. */
  mask_url: string | null;
  model:
    | 'sam3'
    | 'user_box'
    | 'user_box_fallback'
    | 'gemini_native_bbox'
    | string;
  selected: boolean;
  lowConfidence?: boolean;
  /** Item name — passed through to the BboxLayer label pill. */
  name?: string;
}

interface ImageAnnotationStageProps {
  imageUrl: string;
  items: AnnotationItem[];
  onToggleSelected: (id: number) => void;
  /** Set focus on a bbox (tap-to-focus model). */
  onFocusBbox?: (id: number) => void;
  /** Refine a specific item's bbox/mask. */
  onRefineItem?: (id: number) => void;
  /** Delete a specific item. */
  onDeleteItem?: (id: number) => void;
  focusedItemId?: number | null;
  editingItemId?: number | null;
  /** Browse-mode hint — when true, focusing a bbox doesn't hide the
   *  others. Passes through to BboxLayer. Default false (segment-flow
   *  behavior: focus isolates the target). */
  keepUnfocusedVisible?: boolean;
  /** Fill the parent container instead of the default 4:3 box. */
  fillParent?: boolean;
  /** Read-only viewers (Source/Scene view) — when true, render the
   *  bare photo and skip the mask / bbox / controls overlays so the
   *  user can see the clean image. Default false (overlays shown). */
  hideAnnotations?: boolean;
  /** Imperative handle for pinch-zoom: call `.reset()` to snap
   *  back to scale=1 (e.g. from a Fit button). */
  zoomRef?: React.Ref<ZoomableStageHandle>;
  /** Fires when pinch-zoom scale crosses the micro-zoom threshold
   *  in either direction. Parent uses this to enable/disable its
   *  Fit button. */
  onZoomScaleChange?: (scale: number) => void;
  /** Children render function receives the stage's transform +
   *  container ref so refine overlays / scanning hero can read
   *  coord state without each mounting their own listeners. The
   *  ref points to the SCALED inner container, so when the user
   *  pinches the stage, overlay coords stay aligned automatically. */
  children?:
    | React.ReactNode
    | ((ctx: {
        transform: ReturnType<typeof useImageTransform>;
        containerRef: React.RefObject<HTMLDivElement | null>;
      }) => React.ReactNode);
  style?: ViewStyle;
}

export const ImageAnnotationStage: React.FC<ImageAnnotationStageProps> = ({
  imageUrl,
  items,
  onToggleSelected,
  onFocusBbox,
  onRefineItem,
  onDeleteItem,
  focusedItemId,
  editingItemId,
  keepUnfocusedVisible,
  fillParent = false,
  hideAnnotations = false,
  zoomRef,
  onZoomScaleChange,
  children,
  style,
}) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const transform = useImageTransform(imgRef, containerRef);

  const bboxItems: BboxLayerItem[] = items.map((it) => ({
    id: it.id,
    bbox: it.bbox,
    selected: it.selected,
    lowConfidence: it.lowConfidence,
    name: it.name,
  }));
  const maskItems: MaskLayerItem[] = items.map((it) => ({
    id: it.id,
    mask_url: it.mask_url,
    model: it.model,
    selected: it.selected,
  }));
  const controlsItems: ControlsLayerItem[] = items.map((it) => ({
    id: it.id,
    bbox: it.bbox,
    selected: it.selected,
    lowConfidence: it.lowConfidence,
  }));

  const content = (
    <>
      <img
        ref={imgRef}
        src={imageUrl}
        onLoad={transform.onImageLoad}
        crossOrigin="anonymous"
        referrerPolicy="no-referrer"
        style={styles.img}
        alt=""
      />
      {hideAnnotations ? null : (
        <>
          <MaskLayer transform={transform} items={maskItems} />
          <BboxLayer
            transform={transform}
            items={bboxItems}
            focusedItemId={focusedItemId}
            editingItemId={editingItemId}
            onFocusBbox={onFocusBbox}
            keepUnfocusedVisible={keepUnfocusedVisible}
          />
          <ControlsLayer
            transform={transform}
            items={controlsItems}
            onToggleSelected={onToggleSelected}
            onRefine={onRefineItem}
            onDelete={onDeleteItem}
            focusedItemId={focusedItemId}
            editingItemId={editingItemId}
          />
        </>
      )}
      {typeof children === 'function'
        ? (children as any)({ transform, containerRef })
        : children}
    </>
  );

  // Web: wrap in the zoom surface so 2-finger pinch/pan works. The
  // `containerRef` points to the SCALED inner container, not the
  // outer viewport, so all coordinate math stays in visible-pixel
  // space automatically.
  if (Platform.OS === 'web') {
    return (
      <View
        style={[
          fillParent ? styles.stageFill : styles.stageBox,
          style,
        ]}
      >
        <ZoomableStage
          ref={zoomRef}
          innerRef={containerRef}
          onScaleChange={onZoomScaleChange}
        >
          {content}
        </ZoomableStage>
      </View>
    );
  }

  // Native: no zoom support yet. Flat view.
  return (
    <View
      ref={containerRef as any}
      style={[
        fillParent ? styles.stageFill : styles.stageBox,
        style,
      ]}
    >
      {content}
    </View>
  );
};

const styles = StyleSheet.create({
  stageBox: {
    position: 'relative',
    width: '100%',
    aspectRatio: 4 / 3,
    backgroundColor: '#000',
    overflow: 'hidden',
    borderRadius: 14,
  },
  stageFill: {
    position: 'relative',
    flex: 1,
    backgroundColor: '#000',
    overflow: 'hidden',
  },
  img: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
    userSelect: 'none',
    pointerEvents: 'none',
  } as any,
});
